<script>
  import { format } from 'svelte-i18n';
  import { Button, ButtonToolbar, Tooltip } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';
  import { unparse } from 'papaparse';

  import { AssetCard } from '@client/components/asset-card';
  import { BulkDeleteModal } from '@client/components/bulk-delete-modal';
  import { EmptyState } from '@client/components/empty-state';
  import { FilterBar } from '@client/components/filter-bar';
  import { SearchFilter } from '@client/components/filters/search';
  import { Icon } from '@client/components/icon';
  import { LayoutControls } from '@client/components/layout-controls';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { SelectCell, SelectHeaderCell, SelectionControls } from '@client/components/selection-controls';
  import { Spinner } from '@client/components/spinner';
  import { Table } from '@client/components/table';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';
  import { imagesSettingsStore } from '@client/stores/page-settings';
  import { userStore } from '@client/stores/user';

  import { downloadFile } from '@client/utils/download';
  import { getExtention } from '@client/utils/files';
  import { QueryParams } from '@client/utils/query-params';
  import { createSelection, selectedItems } from '@client/utils/selection';

  import { ActionsCell, FilenameCell, FileSizeCell, FileTypeCell, PreviewCell } from './cell-formatters';

  export let refetch = false;
  export let onAddAsset = () => {};

  let exporting = false;
  let renderCardLayout = $appStore.mobile || $imagesSettingsStore.cards || false;
  let sortDirection = 1;
  let sortKey = 'filename';
  let search = QueryParams.get('search') || '';
  let showFilters = true;
  let initialized = false;

  let original = [];
  let filtered = [];

  let selecting = false;
  let showBulkDelete = false;

  const selection = createSelection();

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const queryParams = {};
  const queryFn = async () => {
    const response = await AssetsService.getAllImages(queryParams);

    original = filtered = response;

    refetch = false;

    onChangeSearch(search);

    initialized = true;

    return response;
  };

  const query = createQuery({
    queryKey: ['assets-images', queryParams],
    queryFn
  });

  const onChangeLayout = (which) => {
    renderCardLayout = which;
    $imagesSettingsStore.cards = which;
  };

  const onChangeSearch = (value) => {
    if (!value) {
      filtered = original;
    } else {
      filtered = original.filter((row) => {
        const search = value.toLowerCase();

        return Object.values(row).some((field) => {
          if (typeof field === 'string') {
            return field.toLowerCase().includes(search);
          }

          return false;
        });
      });
    }

    selection.prune(filtered);
  };

  const onDeleteHandler = async (image) => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', { values: { type: $format('label.IMAGE').toLowerCase() } })
    );

    if (confirmed) {
      $appStore.loading = true;

      const response = await AssetsService.deleteImage(image);

      $appStore.loading = false;

      if (response?.status >= 400) {
        notify($format('label.FAILURE.RULENAME_DELETE', { values: { rulename: image.rulename } }), 'error');
        return;
      }

      notify($format('label.SUCCESS.DELETE', { values: { type: image.rulename } }));

      queryFn();
    }
  };

  const onBulkDeleteFinish = async (failed) => {
    const deleted = selectedAssets.length - failed.length;

    if (deleted) {
      notify($format('label.BULK_DELETE.NOTIFY_SUCCESS', { values: { count: deleted } }));
    }

    // Leave only the failures selected so the user can retry them directly.
    if (failed.length) {
      selection.selectAll(failed);
    } else {
      selecting = false;
      selection.clear();
    }

    await queryFn();
  };

  const onClickRow = ({ detail }) => {
    if (!selecting || detail.event.target.closest('input, button, a')) {
      return;
    }

    selection.toggle(detail.row);
  };

  const onExportMetadata = () => {
    $appStore.loading = true;
    exporting = true;

    const exportData = filtered.map((row) => {
      return {
        mimetype: row.mimetype,
        width: row?.metadata?.imageinfo?.width,
        height: row?.metadata?.imageinfo?.height,
        size: row.filesize,
        url: row.imageurl
      };
    });

    exporting = false;
    $appStore.loading = false;

    const data = unparse(exportData);
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });

    downloadFile(blob, `${$userStore.domain}-images-metadata-${new Date().getTime()}.csv`);
  };

  let columns = [
    {
      key: 'preview',
      title: $format('label.PREVIEW'),
      value: ({ filename }) => filename,
      renderComponent: PreviewCell,
      headerClass: 'preview-cell'
    },
    {
      key: 'filename',
      title: $format('label.FILENAME'),
      value: ({ filename }) => filename,
      renderComponent: FilenameCell,
      sortable: true,
      headerClass: 'filename-cell'
    },
    {
      key: 'inline',
      title: $format('label.ASSET_IMAGE_OPTION_INLINE'),
      value: ({ inline }) => (inline ? 'Yes' : 'No'),
      headerClass: 'inline-cell',
      sortable: true
    },
    {
      key: 'filesize',
      title: $format('label.SIZE'),
      value: ({ filesize }) => filesize,
      renderComponent: FileSizeCell,
      headerClass: 'size-cell',
      sortable: true
    },
    {
      key: 'type',
      title: $format('label.TYPE'),
      value: ({ filename }) => getExtention(filename),
      renderComponent: FileTypeCell,
      headerClass: 'type-cell',
      sortable: true
    },
    {
      key: 'actions',
      title: '',
      value: '',
      renderComponent: {
        component: ActionsCell,
        props: {
          onDelete: () => {
            queryFn();
          }
        }
      },
      headerClass: 'actions-cell'
    }
  ];

  $: $appStore.loading = $query.isLoading || $query.isFetching || refetch;
  $: hasImages = initialized && original.length > 0;
  // Rebuilt when `filtered` changes so the header checkbox tracks the visible rows.
  $: selectColumn = {
    key: 'select',
    title: { component: SelectHeaderCell, props: { selection, rows: filtered } },
    value: () => '',
    renderComponent: { component: SelectCell, props: { selection } },
    headerClass: 'select-cell'
  };
  $: tableColumns = selecting ? [selectColumn, ...columns.filter((column) => column.key !== 'actions')] : columns;
  $: selectedAssets = selectedItems(original, $selection);
  $: if (refetch) {
    queryFn();
  }
</script>

<div class="images">
  {#if hasImages}
    <div class="toolbar">
      <FilterBar bind:show={showFilters}>
        <div class="toolbar-right">
          <SearchFilter bind:search onChange={onChangeSearch} />
          {#if filtered.length !== original.length}
            <small class="count">
              {$format('label.MATCHES_X_OF_X_TYPE', {
                values: {
                  filtered: Number(filtered.length).toLocaleString(),
                  total: Number(original.length).toLocaleString(),
                  type: $format('label.IMAGES')
                }
              })}
            </small>
          {/if}

          <ButtonToolbar class="align-items-center gap-2">
            <SelectionControls
              bind:selecting
              {selection}
              rows={filtered}
              id="images"
              onDelete={() => (showBulkDelete = true)}
            />

            {#if filtered.length}
              <Button id="export-metadata-btn" class="d-none d-md-block" on:click={onExportMetadata}>
                {#if exporting}
                  <Spinner styles="border-width: 2px; color: #0087ff; height: 14px; width: 14px;" show inline />
                {:else}
                  <Icon name="download" />
                  <span class="visually-hidden">{$format('label.EXPORT')}</span>
                {/if}
              </Button>
              <Tooltip target="export-metadata-btn" placement="top">{$format('label.EXPORT_METADATA')}</Tooltip>
            {/if}

            <LayoutControls {renderCardLayout} onChange={onChangeLayout} />
          </ButtonToolbar>
        </div>
      </FilterBar>
    </div>
    <div class="items">
      {#if filtered.length}
        {#if $appStore.mobile || renderCardLayout}
          <div class="asset-cards">
            {#each filtered as image}
              <AssetCard
                bind:data={image}
                onDelete={() => onDeleteHandler(image)}
                onSelect={selection.toggle}
                selected={$selection.has(image.rulename)}
                {selecting}
                type="image"
              />
            {/each}
          </div>
        {:else}
          <Table
            id="images-table"
            classNameThead="sticky-header"
            emptyLabel={$format('label.MESSAGE.NO_MATCHES_IMAGES')}
            bind:rows={filtered}
            columns={tableColumns}
            rowClass={(row) => ($selection.has(row.rulename) ? 'bg-primary-subtle' : '')}
            on:clickRow={onClickRow}
            bind:sortBy={sortKey}
            bind:sortOrder={sortDirection}
          />
        {/if}
      {:else}
        <NoMatches message={$format('label.MESSAGE.NO_MATCHES_IMAGES')} />
      {/if}
    </div>
  {:else if initialized}
    <EmptyState
      icon="image"
      heading={$format('label.EMPTY_STATE.IMAGE_HEADING')}
      description={$format('label.EMPTY_STATE.IMAGE_DESCRIPTION')}
      action={$format('label.EMPTY_STATE.IMAGE_ACTION')}
      on:click={onAddAsset}
    />
  {/if}
</div>

<BulkDeleteModal
  bind:show={showBulkDelete}
  items={selectedAssets}
  title={$format('label.BULK_DELETE.CONFIRM_IMAGES', { values: { count: selectedAssets.length } })}
  onDelete={(item) => AssetsService.deleteImage(item)}
  onFinish={onBulkDeleteFinish}
/>

<style lang="scss" src="./images.scss"></style>
